import React, { useState } from 'react'
import { PrimaryButton, SecondaryButton } from './Button'
import { RADOM_COLORS, RADOM_TOKEN_EXPIRY_LOCAL_STORAGE_KEY, RADOM_TOKEN_LOCAL_STORAGE_KEY, WINDOW_SIZE } from '../util/Constants'
import { observer } from 'mobx-react'
import User from '../state/User'
import RadomLogo from '../icons/Radom'
import RadomOldLogo from '../icons/RadomOld'
import GoogleLogo from '../images/Google.svg'
import styled, { keyframes } from 'styled-components'
import Radom from '../state/Radom'
import { useGoogleLogin } from '@react-oauth/google'
import EmailLogin from './EmailLogin'
import Metamask from '../images/Metamask.svg'
import Brave from '../images/Brave.png'
import { TextInput } from './Input'
import { errorToast, successToast } from '../util/Util'

const LoginVideo1 = new URL('../images/login_vid1.mp4', import.meta.url).toString()
const LoginVideo2 = new URL('../images/login_vid2.mp4', import.meta.url).toString()

export enum LoginStep {
  Login,
  StartSignUp,
  VerifySignUp,
  CompleteSignUp,
  ForgotPassword,
  PasswordResetEmailSent,
  PasswordResetCodeVerified,
}

const VidContainer = styled.div`
  display: block;
  overflow: hidden;

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    display: none;
  }
`

const ScaleInSlow = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const Vid = styled.video`
  height: 100%;
  width: 100%;
  object-fit: cover;
  animation: ${ScaleInSlow} 3s ease;
`

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100vh;
`

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  padding: 30px;
  z-index: 1;
  overflow: auto;
  width: 100%;
  height: 100%;
  justify-content: space-between;
  
  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    width: 100%;
  }
`

const LoadingAnimation = keyframes`
  0% {
    stroke-dashoffset: 150;
    opacity: 0;
  }
  50% {
    stroke-dashoffset: 0;
    opacity: 1;
  }
  100% {
    stroke-dashoffset: -150;
    opacity: 0;
  }
`

const LoadingLogo = styled(RadomOldLogo)`
  width: 60px;
  height: auto;

  > path {
    stroke-width: 1px;
    fill: none;
    stroke: url(#gradient);
    stroke-dasharray: 150;
    animation: ${LoadingAnimation} 2s infinite linear;
    transform-origin: center center;
  }
`

const WalletButton = styled(PrimaryButton)`
  background-color: transparent;
  border: 1px solid ${RADOM_COLORS.GRAY_DARK};
  border-radius: 20px;
  width: 100%;
  color: ${RADOM_COLORS.BLACK};
  gap: 10px;
  padding: 15px;
  font-size: 14px;

  &:hover {
    box-shadow: none;
  }
  
  &:focus {
    box-shadow: none;
  }
`

const FooterLink = styled.a`
  color: #2a62ff;
`

if (localStorage.getItem('isWalletLogin')) {
  User.setupWalletConnection()
  User.connect()
}

const WithConnectedWallet = observer(props => {
  const [loginStep, setLoginStep] = useState(LoginStep.Login)

  const login = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: tokenResponse => {
      Radom.loginWithSSO('google', tokenResponse.code)
    },
    onError: err => console.error('errored', err),
    onNonOAuthError: err => console.error('errored', err)

  })

  const verifyOnetimePassword = async (): Promise<void> => {
    if (!token) {
      return errorToast('Enter a valid token')
    }

    try {
      const res = await Radom.verifyOneTimePassword(token)
      Radom.token = res.jwtToken
      localStorage.setItem(RADOM_TOKEN_LOCAL_STORAGE_KEY, res.jwtToken)
      localStorage.setItem(RADOM_TOKEN_EXPIRY_LOCAL_STORAGE_KEY, new Date(res.expiresAt).toString())
      successToast('Successfully logged in')
      Radom.isLoggedIn = true
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
  }

  const [token, setToken] = useState<string>()

  if (Radom.isLoggedIn) {
    return props.children
  }

  return <Container>
    <LoginContainer>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      }}>

        <RadomLogo style={{
          width: 85,
          height: 'auto',
        }} />

        {
          !Radom.requires2fa &&
          <SecondaryButton
            onClick={() => {
              setLoginStep(loginStep == LoginStep.Login ? LoginStep.StartSignUp : LoginStep.Login)
            }}
            style={{ fontSize: 14 }}>
            {loginStep == LoginStep.Login ? 'Create an account' : 'Sign in'}
          </SecondaryButton>
        }
      </div>

      {
        !Radom.requires2fa &&

        <div style={{ display: 'flex', flexDirection: 'column', gap: 30, width: 350, maxWidth: '100%' }}>

          {
            (loginStep == LoginStep.Login || loginStep == LoginStep.StartSignUp) &&

            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, alignItems: 'center' }}>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                color: RADOM_COLORS.NEW_GRAY_1,
                marginBottom: 10,
                alignItems: 'center',
                userSelect: 'none'
              }}>

                <h1 style={{ fontSize: 26, fontWeight: 500, textAlign: 'center', margin: 0 }}>
                  {loginStep == LoginStep.Login ? 'Sign into Radom' : 'Create a Radom account'}
                </h1>

                {
                  loginStep == LoginStep.StartSignUp &&
                  <span style={{ fontSize: 16, textAlign: 'center', opacity: 0.8, fontWeight: 300 }}>
                    Ready to start accepting crypto payments? Create an account to get started.
                  </span>
                }
              </div>
            </div>
          }

          {
            (loginStep == LoginStep.Login || loginStep == LoginStep.StartSignUp) &&
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
                <WalletButton
                  isLoading={User.isLoading}
                  onClick={() => login()}>
                  <img src={GoogleLogo as string} style={{ height: 18, width: 'auto' }} />
                  <span>Sign in with Google</span>
                </WalletButton>

                <WalletButton
                  disabled={User.isLoading}
                  onClick={async () => {
                    User.setupWalletConnection()
                    await User.connect()
                      .then(async () => await Radom.login())
                      .then(() => {
                        localStorage.setItem('isWalletLogin', 'true')
                      })
                      .catch(() => { })
                  }}>
                  <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>

                    <img src={Metamask as any} style={{ width: 'auto', height: 18 }} />
                    <img src={Brave} style={{ width: 'auto', height: 18 }} />
                  </div>
                  {/* <Plug style={{ width: 15, height: 15, position: 'absolute', left: 10 }} stroke={RADOM_COLORS.BLUE} strokeWidth={3} /> */}
                  <span>Connect wallet</span>
                </WalletButton>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ height: 1, flex: 1, flexGrow: 1, background: 'black', opacity: 0.1 }} />
                <span style={{ fontSize: 12, textTransform: 'uppercase', opacity: 0.33 }}>Or</span>
                <div style={{ height: 1, flex: 1, flexGrow: 1, background: 'black', opacity: 0.1 }} />
              </div>
            </>
          }

          <EmailLogin step={loginStep} setStep={(step) => { setLoginStep(step) }} />

          {
            (loginStep === LoginStep.Login || loginStep == LoginStep.StartSignUp) &&
            <div style={{ fontSize: 13, textAlign: 'center', padding: '0 20px', color: RADOM_COLORS.GRAY_DARKEST, fontWeight: 300 }}>
              By creating an account you agree to the <FooterLink href="https://www.radom.com/terms" target='_blank'>Terms & Conditions</FooterLink> and <FooterLink href="https://www.radom.com/privacy" target='_blank'>Privacy Policy</FooterLink>.
            </div>
          }

        </div>
      }

      {
        Radom.requires2fa &&
        <div style={{ display: 'flex', flexDirection: 'column', gap: 30, width: 400, maxWidth: '100%' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, alignItems: 'center' }}>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              color: RADOM_COLORS.NEW_GRAY_1,
              marginBottom: 10,
              alignItems: 'center'
            }}>

              <span style={{ fontSize: 20, fontWeight: 500, textAlign: 'center' }}>
                Two-factor authentication
              </span>
              <span style={{ fontSize: 14, textAlign: 'center' }}>
                Open the two-step verification app on your mobile device to get your verification code.
              </span>
            </div>
          </div>

          <form
            onSubmit={e => {
              e.preventDefault(); verifyOnetimePassword()
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <TextInput
              onSubmit={verifyOnetimePassword}
              autoFocus
              value={token}
              onChange={e => setToken(e.target.value)}
              placeholder='Authentication code' />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

              <PrimaryButton
                type='button'
                isLoading={User.isLoading}
                onClick={verifyOnetimePassword}>
                <span>Authenticate</span>
              </PrimaryButton>

              <SecondaryButton
                type='button'
                onClick={() => {
                  localStorage.clear()
                  window.location = '/' as any
                }}>
                Back to login
              </SecondaryButton>
            </div>

          </form>

        </div>
      }

      <div></div>
    </LoginContainer>

    <VidContainer style={{ width: '35%', minWidth: 300, height: '100vh', background: 'white', pointerEvents: 'none', userSelect: 'none' }}>
      {loginStep == LoginStep.Login &&
        <Vid autoPlay muted loop>
          <source src={LoginVideo2} type="video/mp4" />
        </Vid>
      }

      {loginStep != LoginStep.Login &&
        <Vid autoPlay muted loop>
          <source src={LoginVideo1} type="video/mp4" />
        </Vid>
      }
    </VidContainer>
  </Container>
})

export default WithConnectedWallet
